import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
} from "@mui/material";
import {
  People as PeopleIcon,
  Web as WebIcon,
  Storage as StorageIcon,
  Timeline as TimelineIcon,
  PersonAdd,
  AddCircle,
} from "@mui/icons-material";
import {
  fetchAdminDashboard,
  clearAdminError,
} from "../../redux/slices/adminSlice";
import { SPACING } from "../../theme/constants";

const StatCard = ({ title, value, subtitle, icon, color = "primary" }) => (
  <Card sx={{ height: "100%" }}>
    <CardContent sx={{ p: 2.5 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            backgroundColor: `${color}.light`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {React.cloneElement(icon, {
            sx: { color: `${color}.main`, fontSize: 24 },
          })}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontWeight: 500,
              textTransform: "uppercase",
              letterSpacing: 0.5,
              fontSize: "0.675rem",
            }}
          >
            {title}
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {value ?? "—"}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const AdminDashboard = () => {
  const dispatch = useDispatch();
  const { dashboard, loading, error } = useSelector((state) => state.admin);

  useEffect(() => {
    dispatch(fetchAdminDashboard());
  }, [dispatch]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => dispatch(clearAdminError()), 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  if (loading && !dashboard) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  const users = dashboard?.users || {};
  const sites = dashboard?.sites || {};
  const backups = dashboard?.backups || {};
  const activities = dashboard?.activities || {};

  return (
    <Box>
      <Box sx={{ mb: SPACING.lg }}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: SPACING.xs }}>
          Admin Dashboard
        </Typography>
        <Typography variant="body2" color="text.secondary">
          System overview and key metrics
        </Typography>
      </Box>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => dispatch(clearAdminError())}
        >
          {error}
        </Alert>
      )}

      {/* User Stats */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Users
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Users"
            value={users.total}
            subtitle={`${users.active || 0} active`}
            icon={<PeopleIcon />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Admin Users"
            value={users.admins}
            icon={<PeopleIcon />}
            color="error"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="New Signups (30d)"
            value={users.recentSignups}
            icon={<PersonAdd />}
            color="success"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Inactive Users"
            value={users.inactive}
            icon={<PeopleIcon />}
            color="warning"
          />
        </Grid>
      </Grid>

      {/* Site Stats */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        Sites
      </Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Sites"
            value={sites.total}
            subtitle={`${sites.active || 0} active`}
            icon={<WebIcon />}
            color="primary"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Pending Sites"
            value={sites.pending}
            icon={<WebIcon />}
            color="warning"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Suspended Sites"
            value={sites.suspended}
            icon={<WebIcon />}
            color="error"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="New Sites (30d)"
            value={sites.recentCreated}
            icon={<AddCircle />}
            color="success"
          />
        </Grid>
      </Grid>

      {/* System Stats */}
      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
        System
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Total Backups"
            value={backups.total}
            icon={<StorageIcon />}
            color="info"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Activities (24h)"
            value={activities.last24Hours}
            icon={<TimelineIcon />}
            color="secondary"
          />
        </Grid>
      </Grid>
    </Box>
  );
};

export default AdminDashboard;
